// Turns the AI audit's proposal into approve/deny steps, and applies the
// officer's decisions. Pure functions: no React, no network.
//
// A step is one section ("group"): the fields the AI wants to add to it, the
// existing fields whose section, label, or type it wants to change, and the
// section's context. Discarding a step removes its additions and puts its
// changed fields back exactly as they were.
import type { FormFieldDefinition, FormSection } from "../../db/schema";

export interface FieldChange {
  before: FormFieldDefinition;
  after: FormFieldDefinition;
  // Human-readable lines, e.g. `Label: "Text12" to "Address"`.
  notes: string[];
}

export interface ReviewGroup {
  key: string; // the proposed section name ("" = no section)
  title: string;
  context?: string;
  contextChanged: boolean;
  added: FormFieldDefinition[];
  changed: FieldChange[];
  // Existing fields the AI doubts (confidence < 0.4).
  flagged: FormFieldDefinition[];
  firstPage: number;
}

export interface ReviewProposal {
  fields: FormFieldDefinition[];
  sections: FormSection[];
}

const UNGROUPED = "Ungrouped";
const FLAG_BELOW = 0.4;

function describeChange(before: FormFieldDefinition, after: FormFieldDefinition): string[] {
  const notes: string[] = [];
  if ((before.section ?? "") !== (after.section ?? "")) {
    notes.push(
      before.section ? `Section: "${before.section}" to "${after.section ?? UNGROUPED}"` : `Added to this section`,
    );
  }
  if (before.label !== after.label) notes.push(`Label: "${before.label}" to "${after.label}"`);
  if (before.type !== after.type) notes.push(`Type: ${before.type} to ${after.type}`);
  if (!before.additionalContext && after.additionalContext) notes.push("Adds a context note");
  return notes;
}

export function buildGroups(
  before: FormFieldDefinition[],
  beforeSections: FormSection[],
  proposal: ReviewProposal,
): ReviewGroup[] {
  const beforeById = new Map(before.map((f) => [f.id, f]));
  const beforeContext = new Map(beforeSections.map((s) => [s.name, s.context ?? ""]));
  const proposedContext = new Map(proposal.sections.map((s) => [s.name, s.context ?? ""]));
  const groups = new Map<string, ReviewGroup>();

  const groupFor = (field: FormFieldDefinition): ReviewGroup => {
    const key = field.section ?? "";
    let g = groups.get(key);
    if (!g) {
      const ctx = proposedContext.get(key) || undefined;
      g = {
        key,
        title: key || UNGROUPED,
        context: ctx,
        contextChanged: !!ctx && ctx !== (beforeContext.get(key) ?? ""),
        added: [],
        changed: [],
        flagged: [],
        firstPage: field.page,
      };
      groups.set(key, g);
    }
    g.firstPage = Math.min(g.firstPage, field.page);
    return g;
  };

  for (const f of proposal.fields) {
    const old = beforeById.get(f.id);
    if (!old) {
      groupFor(f).added.push(f);
      continue;
    }
    const notes = describeChange(old, f);
    if (notes.length) groupFor(f).changed.push({ before: old, after: f, notes });
    if (typeof f.confidence === "number" && f.confidence < FLAG_BELOW) groupFor(f).flagged.push(f);
  }

  return [...groups.values()]
    .filter((g) => g.added.length || g.changed.length || g.flagged.length || g.contextChanged)
    .sort((a, b) => a.firstPage - b.firstPage);
}

// The fields and sections that result from approving some groups and
// discarding every other one. For the live preview while reviewing, pass the
// groups approved so far plus the one on screen.
export function composeResult(
  before: FormFieldDefinition[],
  beforeSections: FormSection[],
  proposal: ReviewProposal,
  groups: ReviewGroup[],
  accepted: Set<string>,
): { fields: FormFieldDefinition[]; sections: FormSection[] } {
  const beforeById = new Map(before.map((f) => [f.id, f]));
  const rejected = new Set(groups.filter((g) => !accepted.has(g.key)).map((g) => g.key));

  const fields: FormFieldDefinition[] = [];
  for (const f of proposal.fields) {
    const key = f.section ?? "";
    const old = beforeById.get(f.id);
    if (!rejected.has(key)) {
      fields.push(f);
    } else if (old) {
      fields.push(old); // changed or flagged in a discarded group: back to what it was
    }
    // an addition in a discarded group is dropped
  }

  // Renumber `order` within each section, keeping relative order.
  const bySection = new Map<string, FormFieldDefinition[]>();
  for (const f of fields) {
    const k = f.section ?? "";
    bySection.set(k, [...(bySection.get(k) ?? []), f]);
  }
  const orderOf = new Map<string, number>();
  bySection.forEach((list) =>
    [...list]
      .sort((a, b) => a.order - b.order || a.page - b.page || a.rect.y - b.rect.y || a.rect.x - b.rect.x)
      .forEach((f, i) => orderOf.set(f.id, i + 1)),
  );
  const ordered = fields.map((f) => ({ ...f, order: orderOf.get(f.id)! }));

  // Sections: approved groups bring the AI's context; everything else keeps
  // the officer's. Empty sections the officer made are preserved.
  const present = new Set(ordered.map((f) => f.section).filter((s): s is string => !!s));
  const proposedContext = new Map(proposal.sections.map((s) => [s.name, s.context]));
  const beforeContext = new Map(beforeSections.map((s) => [s.name, s.context]));
  const names: string[] = [];
  const push = (n: string) => n && !names.includes(n) && names.push(n);
  [...ordered]
    .sort((a, b) => a.page - b.page || a.rect.y - b.rect.y || a.rect.x - b.rect.x)
    .forEach((f) => f.section && push(f.section));
  beforeSections.forEach((s) => push(s.name));
  const sections = names
    .filter((n) => present.has(n) || beforeContext.has(n))
    .map((n) => {
      const context = accepted.has(n) ? proposedContext.get(n) || beforeContext.get(n) : beforeContext.get(n);
      return context ? { name: n, context } : { name: n };
    });

  return { fields: ordered, sections };
}
